import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { USER_TYPE } from '../constants/user.constants';
import {
  paginatedUsersSchema,
  userDetailSchema,
  reportingAuthorityListSchema,
  type UserListParams,
  type UserListSearch,
  type CreateUserPayload,
  type UpdateUserRequest,
  type ResetPasswordPayload,
  type PaginatedUsers,
  type UserDetail,
  type ReportingAuthority,
} from '../types/user.types';

const DEFAULT_USER_PAGE_SIZE = 20;

export const userQueryKeys = {
  all: () => ['users'] as const,
  lists: () => [...userQueryKeys.all(), 'list'] as const,
  list: (params: UserListParams) => [...userQueryKeys.lists(), params] as const,
  listInfinite: (params: UserListSearch) => [...userQueryKeys.lists(), 'infinite', params] as const,
  detail: (id: string) => [...userQueryKeys.all(), 'detail', id] as const,
  reportingAuthorities: (userTypeId: number) =>
    [...userQueryKeys.all(), 'reporting-authorities', userTypeId] as const,
};

function buildUserListRequestParams(params: UserListParams) {
  const { userTypeId, ...rest } = params;
  return {
    ...rest,
    ...(Array.isArray(userTypeId)
      ? { userTypeId: userTypeId.join(',') }
      : userTypeId !== undefined && { userTypeId }),
  };
}

async function fetchUserList(params: UserListParams): Promise<PaginatedUsers> {
  const res = await apiClient.get(API.users.list, {
    params: buildUserListRequestParams(params),
  });
  return paginatedUsersSchema.parse(res.data);
}

export const userListQueryOptions = (params: UserListParams) =>
  queryOptions({
    queryKey: userQueryKeys.list(params),
    queryFn: () => fetchUserList(params),
    staleTime: STALE_TIME,
  });

export const userListInfiniteQueryOptions = (params: UserListSearch) =>
  infiniteQueryOptions({
    queryKey: userQueryKeys.listInfinite(params),
    queryFn: ({ pageParam }) =>
      fetchUserList({ ...params, page: pageParam, limit: DEFAULT_USER_PAGE_SIZE }),
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const reportingAuthoritiesQueryOptions = (userTypeId: number) =>
  queryOptions({
    queryKey: userQueryKeys.reportingAuthorities(userTypeId),
    queryFn: async (): Promise<ReportingAuthority[]> => {
      const res = await apiClient.get(API.users.reportingAuthorities, {
        params: { userTypeId },
      });
      return reportingAuthorityListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const zonalManagersQueryOptions = () => reportingAuthoritiesQueryOptions(USER_TYPE.AGENT);

export const areaManagersQueryOptions = () => reportingAuthoritiesQueryOptions(USER_TYPE.TSO);

export const regionalSalesManagersQueryOptions = () =>
  reportingAuthoritiesQueryOptions(USER_TYPE.ASM);

export const userDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: userQueryKeys.detail(id),
    queryFn: async (): Promise<UserDetail> => {
      const res = await apiClient.get(API.users.detail(id));
      return userDetailSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function createUser(payload: CreateUserPayload): Promise<void> {
  await apiClient.post(API.users.create, payload);
}

export async function updateUser(id: string, payload: UpdateUserRequest): Promise<void> {
  await apiClient.put(API.users.update(id), payload);
}

export async function deleteUser(id: string): Promise<void> {
  await apiClient.delete(API.users.delete(id));
}

export async function resetUserPassword(id: string, payload: ResetPasswordPayload): Promise<void> {
  await apiClient.patch(API.users.resetPassword(id), payload);
}
